<!-- A widget displaying a Store and its DecoderEntries below it. -->

<template>
  <div>
    <!-- Header -->
    <detail-entries-header
      v-if="showHeader && sortedWrappers.length > 0"
      :prefix1="activeOnly ? $t('activeDecoders') : $t('futureDecoders')"
      :num1="filteredEntriesWithoutDeactivatedTagEntries.length"
      :prefix2="$t('deactivated')"
      :num2="deactivatedTagEntries.length"
      show-expander
      :all-expanded.sync="allExpanded"
    />
    <!-- Stores -->
    <v-list
      v-for="(wrapper, wrapperIndex) in sortedWrappers"
      :key="wrapper.store.id"
      dense
      class="mb-2"
    >
      <v-list-tile
        avatar
        class="store-tile"
        @click.stop="toggleWrapperExpanded(wrapper)"
      >
        <v-list-tile-avatar>
          <v-icon
            large
            class="msr--text"
          >
            location_on
          </v-icon>
        </v-list-tile-avatar>
        <v-list-tile-content>
          <v-list-tile-sub-title>
            <small>{{ wrapper.store.name }}</small>
          </v-list-tile-sub-title>
          <v-list-tile-title>
            <span class="grey--text text--darken-3">
              <span style="font-size: 1.25em">
                <strong>{{ wrapper.store.city }}</strong>
                {{ wrapper.store.location || '' }}
              </span>
            </span>
            <v-layout
              row
              wrap
            >
              <div v-if="wrapper.store.address">
                {{ wrapper.store.address }}
              </div>
              <v-spacer />
              <v-slide-y-reverse-transition>
                <div
                  v-if="
                    wrapper.storeSpotUsage && !wrapper.storeSpotUsage.fully_used
                  "
                  class="grey--text add-more-decoders-clickarea"
                  :title="getSpotUsageTooltip(wrapper.storeSpotUsage)"
                  @click.stop="
                    goToAddDecoderToSpotForStore(
                      $route.params.ctype,
                      $route.params.spotId,
                      wrapper.store
                    )
                  "
                >
                  <v-layout
                    row
                    wrap
                    align-center
                  >
                    <div class="mr-1 pt-1">
                      <v-icon
                        v-if="$route.params.ctype === 'tv'"
                        class="decodertype-icon black-on-hover"
                      >
                        tv
                      </v-icon>
                      <v-icon
                        v-if="$route.params.ctype === 'radio'"
                        class="decodertype-icon black-on-hover"
                      >
                        radio
                      </v-icon>
                    </div>
                    <div class="black-on-hover">
                      {{ wrapper.storeSpotUsage.num_decoders_running_spot }} /
                      {{
                        wrapper.storeSpotUsage.num_decoders_compatible_to_spot
                      }}
                    </div>
                    <v-btn
                      icon
                      dark
                      style="width: 18px; height: 18px"
                      class="msr ma-0 ml-2"
                    >
                      <v-icon style="font-size: 14px">
                        add
                      </v-icon>
                    </v-btn>
                  </v-layout>
                </div>
              </v-slide-y-reverse-transition>
            </v-layout>
          </v-list-tile-title>
        </v-list-tile-content>
        <v-list-tile-action>
          <v-icon
            medium
            class="grey--text"
            :class="{flipped: wrapper.expanded}"
          >
            keyboard_arrow_down
          </v-icon>
        </v-list-tile-action>
      </v-list-tile>
      <!-- Store Entries -->
      <transition
        name="list-transition"
        mode="out-in"
      >
        <div
          v-if="wrapper.expanded"
          class="list pa-0 mt-2"
        >
          <spot-decoder-entry
            v-for="entry in wrapper.filteredEntries"
            :key="entry.id"
            class="inset-left-within-container"
            :entry="entry"
            :show-actions="!entry.decoder.read_only"
            :show-selection-checkbox="showSelectionCheckboxes"
            :show-divider="false"
            :show-timing="!entry.spot.closingtime"
            :update-allowed="!(entry.spot.expired || entry.decoder.read_only)"
            @deleted="$emit('entry-deleted')()"
            @changed="$emit('changed')"
          />
        </div>
      </transition>
      <v-divider
        v-if="wrapperIndex !== sortedWrappers.length - 1"
        class="mt-1"
      />
    </v-list>
  </div>
</template>

<script>
import groupBy from 'lodash.groupby';
import orderBy from 'lodash.orderby';

import commonEntry from '@/mixins/commonEntry.js';
import identifiers from '@/mixins/identifiers.js';
import selectedTimeframes from '@/mixins/selectedTimeframes.js';

import DetailEntriesHeader from '@/components/DetailEntriesHeader';
import SpotDecoderEntry from '@/components/SpotDecoderEntry';

/**
 * An object bundling a Store with its Decoder's SpotEntries.
 *
 * Entries should previously have been filtered (active/future).
 */
class StoreEntriesWrapper {
  constructor(store, filteredEntries, expanded, storeSpotUsage = null) {
    this.store = store;
    this.filteredEntries = filteredEntries;
    this.expanded = expanded;

    this.storeSpotUsage = storeSpotUsage;
  }
}

export default {
  components: {
    DetailEntriesHeader,
    SpotDecoderEntry,
  },
  mixins: [commonEntry, identifiers, selectedTimeframes],
  props: {
    entries: {
      type: Array,
      required: true,
    },
    /**
     * An optional object that is used to display usage context.
     */
    spotUsageInStores: {
      type: Object,
      default: null,
    },
    showHeader: {
      type: Boolean,
      default: false,
    },
    showDivider: {
      type: Boolean,
      default: true,
    },
    showSelectionCheckboxes: {
      type: Boolean,
      default: false,
    },
    showExpansionControl: {
      type: Boolean,
      default: true,
    },
    activeOnly: {
      type: Boolean,
      default: false,
    },
    futureOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sortedWrappers: [],
    };
  },
  computed: {
    allExpanded: {
      get() {
        const expandedWrappers = this.sortedWrappers.filter(
          (wrapper) => wrapper.expanded
        );
        return expandedWrappers.length === this.sortedWrappers.length;
      },
      set(expanded) {
        const newExpanded = expanded || !this.allExpanded;
        this.sortedWrappers.map((wrapper) => (wrapper.expanded = newExpanded));
      },
    },
    filteredEntries() {
      return this.getFilteredEntries(this.entries);
    },
    filteredEntriesWithoutDeactivatedTagEntries() {
      const excludeDeactivatedTagEntries = true;
      return this.getFilteredEntries(
        this.entries,
        excludeDeactivatedTagEntries
      );
    },
    deactivatedTagEntries() {
      return this.getDeactivatedTagEntries(this.entries);
    },
  },
  watch: {
    /**
     * This only exists to properly initialize the array.
     *
     * If done within mounted() it may not yet have resolved into a list.
     */
    entries: function () {
      this.initializeWrappers();
    },
    /**
     * As this.sortedWrappers does not auto-update with filtering, we
     * trigger a recalculation here when the timeframe changes.
     */
    selectedTimeframes: function () {
      this.initializeWrappers();
    },
  },
  mounted() {
    if (this.activeOnly && this.futureOnly) {
      throw 'Set either "activeOnly" or "futureOnly" prop, cannot set both!';
    }

    this.initializeWrappers();
  },
  methods: {
    getSpotUsageTooltip(storeSpotUsage) {
      if (!storeSpotUsage) {
        return '';
      }
      return this.$t('runsOnPlayers')
        .replace('{{ numRunning }}', storeSpotUsage.num_decoders_running_spot)
        .replace(
          '{{ numCompatible }}',
          storeSpotUsage.num_decoders_compatible_to_spot
        );
    },
    /**
     * Analyze given entries and wrap them in helper objects.
     *
     * Each wrapper wraps a Store and all SpotEntries for it, marking
     * them as being either 'active' or 'future/planned' and
     * remembering the Store's expansion state.
     *
     * Note: Wrappers that have no entries due to sorting are skipped
     *   from the result!
     *
     * This is intentionally not a computed property so that the
     * wrapper objects are reactive and updating them reflects in the UI.
     *
     * Args:
     *   keepExpansionStates (Boolean): Optional, true by default.
     *     Since the entries here are grouped below their Store which
     *     can be expanded/collapsed, we try to 'remember' the expansion
     *     states here. Otherwise any change to the list would collapse
     *     all wrappers, which can be quite annoying for the user.
     */
    createSortedWrappers(keepExpansionStates = true) {
      let wrappers = [];
      let entriesGroupedByStore = groupBy(this.entries, 'decoder.store.id');

      let storeIds = Object.keys(entriesGroupedByStore);
      storeIds = orderBy(storeIds, (storeId) => {
        let entries = entriesGroupedByStore[storeId];
        let firstEntry = entries[0];
        return firstEntry.decoder.store.city;
      });

      storeIds.forEach((storeId) => {
        let entries = entriesGroupedByStore[storeId];
        let firstEntry = entries[0];
        let store = firstEntry.decoder.store;

        // Apply futureOnly/activeOnly filtering.
        let wrapperEntries = this.getFilteredEntries(entries);
        if (!wrapperEntries.length) {
          return;
        }

        let expanded = false;
        if (keepExpansionStates) {
          const oldWrapper = this.sortedWrappers.find(
            (wrapper) => wrapper.store.id == storeId
          );
          if (oldWrapper) {
            expanded = oldWrapper.expanded;
          }
        }

        let storeSpotUsage = null;
        if (this.spotUsageInStores) {
          storeSpotUsage = this.spotUsageInStores.usage[store.id];
        }
        let wrapper = new StoreEntriesWrapper(
          store,
          wrapperEntries,
          expanded,
          storeSpotUsage
        );
        wrappers.push(wrapper);
      });
      return wrappers;
    },
    toggleWrapperExpanded(wrapper) {
      if (!this.showExpansionControl) {
        return;
      }
      wrapper.expanded = !wrapper.expanded;
    },
    /**
     * Return either active or future entries.
     *
     * Also filters by selected timeframe.
     */
    getFilteredEntries(entries, excludeDeactivatedTagEntries = false) {
      let filtered = entries;
      if (this.futureOnly) {
        filtered = this.getFutureEntries(entries);
      }
      if (this.activeOnly) {
        filtered = this.getActiveEntries(entries);
      }

      if (excludeDeactivatedTagEntries) {
        filtered = filtered.filter(
          (entry) => !this.isDeactivatedTagEntry(entry)
        );
      }

      filtered = this.filterEntriesForSelectedTimeframes(filtered);
      return filtered;
    },
    /**
     * Preselect available Decoders filtered by a specific Store name.
     */
    goToAddDecoderToSpotForStore(ctype, spotId, store) {
      const route = {
        path: '/spot/' + ctype + '/' + spotId + '/add',
        query: {
          search: store.city + ' ' + store.name,
          selected: true,
        },
      };
      this.$router.push(route);
    },
    initializeWrappers() {
      this.sortedWrappers = this.createSortedWrappers();
      if (this.sortedWrappers.length <= 3) {
        this.allExpanded = true;
      }
    },
  },
};
</script>

<style scoped>
.list {
  transform-origin: top;
}

.list-transition-enter-active,
.list-transition-leave-active {
  transition: all 0.2s ease-in-out;
}
.list-transition-enter,
.list-transition-leave-to {
  opacity: 0;
  transform: scaleY(0);
}

.decodertype-icon {
  font-size: 1.25em !important;
}

.add-more-decoders-clickarea:hover .black-on-hover {
  color: black !important;
}
</style>



// WEBPACK FOOTER //
// src/components/SpotDetail/SpotDetailEntries.vue